import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, Plus, ArrowLeft, Sparkles, Briefcase, 
  GraduationCap, Heart, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import VoiceInput from '@/components/VoiceInput';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'work', label: 'Work Achievement', icon: Briefcase, color: 'bg-blue-500' },
  { value: 'academic', label: 'Academic Achievement', icon: GraduationCap, color: 'bg-purple-500' },
  { value: 'personal', label: 'Personal Growth', icon: Heart, color: 'bg-pink-500' },
  { value: 'other', label: 'Other Win', icon: Star, color: 'bg-amber-500' }
];

const AccomplishmentsPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [accomplishments, setAccomplishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'work'
  });

  useEffect(() => {
    fetchAccomplishments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccomplishments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accomplishments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setAccomplishments(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const createAccomplishment = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/accomplishments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success(`Achievement added! +${data.points_earned} points 🏆`);
        setShowModal(false);
        setFormData({ title: '', description: '', category: 'work' });
        fetchAccomplishments();
      }
    } catch (error) {
      toast.error('Failed to add accomplishment');
    }
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[3];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 text-white rounded-b-3xl" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold" style={{ color: "white" }}>My Accomplishments</h1>
              <p style={{ color: "rgba(255,255,255,0.9)" }}>Celebrate your wins!</p>
            </div>
          </div>

          <div className="bg-white/20 rounded-2xl p-4 backdrop-blur">
            <p className="text-2xl font-bold">{accomplishments.length}</p>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>Total Achievements Recorded</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Add Button */}
        <Button
          onClick={() => setShowModal(true)}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-lg"
          data-testid="add-accomplishment-button"
        >
          <Plus className="w-6 h-6 mr-2" />
          Add New Accomplishment
        </Button>

        {/* Info Card */}
        <Card className="p-4 bg-purple-50 border-purple-200 rounded-2xl">
          <div className="flex items-start gap-3">
            <Trophy className="w-6 h-6 text-purple-600 mt-1" />
            <div>
              <p className="font-semibold text-purple-900">Share Your Wins!</p>
              <p className="text-sm text-purple-700">
                Every accomplishment you add earns 15 points and gets posted to the Wins Wall!
              </p>
            </div>
          </div>
        </Card>

        {/* Accomplishments List */}
        {accomplishments.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl">
            <Trophy className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No accomplishments yet</h2>
            <p className="text-muted-foreground">
              Add your first win to get started!
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {accomplishments.map((acc, index) => {
              const categoryInfo = getCategoryInfo(acc.category);
              const CategoryIcon = categoryInfo.icon;

              return (
                <motion.div
                  key={acc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-5 rounded-3xl">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${categoryInfo.color} rounded-2xl flex items-center justify-center`}>
                        <CategoryIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-lg">{acc.title}</h3>
                          <Badge variant="outline">{categoryInfo.label}</Badge>
                        </div>
                        <p className="text-muted-foreground">{acc.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(acc.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Accomplishment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Trophy className="w-6 h-6 text-purple-500" />
              Add Accomplishment
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="font-semibold">What did you accomplish? *</Label>
              <Input
                placeholder="e.g., Got promoted, Finished a course, Helped a coworker"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="rounded-xl mt-1"
                data-testid="accomplishment-title-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Tell us more about it *</Label>
                <VoiceInput size="sm" onTranscript={(text) => setFormData(prev => ({...prev, description: prev.description ? prev.description + ' ' + text : text}))} />
              </div>
              <Textarea
                placeholder="Describe your achievement and why it matters to you..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="rounded-xl mt-1 min-h-[100px]"
                data-testid="accomplishment-description-input"
              />
            </div>

            <div>
              <Label className="font-semibold">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={createAccomplishment}
              className="w-full h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
              data-testid="submit-accomplishment-button"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Add Accomplishment (+15 points)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Navigation role="youth" />
    </div>
  );
};

export default AccomplishmentsPage;
